import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '../../core/config';

/**
 * Axios config with the two flags the ported tree and this adapter use.
 *
 * The shared instance reads `skipLoader` (do not raise the global spinner) and
 * `skipErrorToast` (do not toast a failure) off the config. They are declared here so a
 * caller can pass them without an unsafe cast.
 */
export type LegacyRequestConfig = AxiosRequestConfig & {
  skipLoader?: boolean;
  skipErrorToast?: boolean;
};

/**
 * Translation layer between the ported PoseidonFrontend tree (`src/main/**` +
 * `src/pages-old/**`) and PlatformAPI's v1 surface.
 *
 * ## Why this exists instead of editing the copied screens
 *
 * The port instruction for this app was: copy the old screens as they are and fix the
 * URLs and paths. Most of that fix is a straight retarget of `ApiConstants`, which the
 * ported screens already route through. Three classes of call cannot be expressed as a
 * constant, though, because the difference is a **verb**, a **path-parameter vs
 * body-field** choice, or a **body/response field rename**:
 *
 *   1. Legacy routes carry the id in the body (`{ Id }`) or in a query string
 *      (`?roleId=`) because the monolith had flat action-style endpoints
 *      (`api/utility/role/delete`). PlatformAPI is resource-shaped
 *      (`DELETE /authorization/roles/{roleId}`).
 *   2. Legacy grant rows are `read`/`write`, the Platform's are `canRead`/`canWrite`.
 *   3. Legacy screens branch on the HTTP status (`response.status === 200`), while
 *      PlatformAPI correctly answers `201`/`204` for a create/update.
 *
 * Rewriting eight screens and ~30 call sites by hand for those three things would put the
 * port at permanent risk of drift from the source project and would be impossible to
 * review. Doing it once, here, keeps every copied file byte-identical and makes the whole
 * legacy→Platform contract auditable in a single reading.
 *
 * ## Two responsibilities, in order
 *
 * `mapRequest`  legacy call → the request PlatformAPI actually serves.
 * `mapResponse` PlatformAPI payload → the shape the ported screens read.
 *
 * Anything that is not recognised passes through untouched. That matters: a missed rule
 * fails loudly as a 404 on `/api/v1/platform/<legacy path>`, which is how the remaining
 * gaps are found, rather than silently reaching some unrelated handler.
 *
 * ## Known gaps marked here
 *
 * A few legacy calls have no Platform counterpart yet. Rather than let each one surface as
 * an error toast (the axios instance toasts every failed response), they are flagged
 * `skipErrorToast` and, where the UI shows a spinner, `skipLoader`. The gaps are listed in
 * `Architech-Upgrade 5.0/07-component-ownership-platform-vs-restaurant.md`.
 */

/** Legacy → Platform path rewrites for the hard-coded literals the copied tree still has. */
const LEGACY_PATH_REWRITES: Array<[RegExp, string]> = [
  /*
   * `main/utils/fileUrl.ts` and `entityGallery.ts` — the Platform has no media service yet, so
   * `media/*` is a reserved namespace rather than a served one. Every legacy `/api/upload/*`
   * path is folded onto it so there is one agreed prefix for the eventual service instead of a
   * scattering of monolith URLs. See gap 1 in the ownership doc, section 6.5.
   */
  [/^\/api\/upload\/metadata\/by-entity-ids$/, 'media/metadata/by-entity-ids'],
  [/^\/api\/upload\/metadata\/by-entity$/, 'media/metadata/by-entity'],
  [/^\/api\/upload\/files\/by-entity$/, 'media/files/by-entity'],
  [/^\/api\/upload\/download\/by-id$/, 'media/download/by-id'],
  [/^\/api\/upload\/delete\/by-id$/, 'media/delete/by-id'],
  [/^\/api\/upload\/download$/, 'media/download'],
  [/^\/api\/upload\/upload$/, 'media/upload'],
  // `pages-old/mainLanding/Landing/NotificationContext.tsx`
  [/^\/api\/notification\/count$/, 'notifications/count'],
  [/^\/api\/notification\/read-all$/, 'notifications/read-all'],
  [/^\/api\/notification\/read$/, 'notifications/read'],
  [/^\/api\/notification$/, 'notifications'],
  // `main/initializers/app.ts` (not used by the bridge, kept correct anyway)
  [/^\/api\/company$/, 'organization'],
  // `pages-old/mainLanding` keeps its own literal endpoint table. `posts` and
  // `posts/create` map onto the Platform's feed collection; the sub-resource operations
  // (`comments`, `like`, `report`) are deliberately NOT in `KNOWN_MISSING`, so if a ported
  // call site still uses one it fails loudly on a legible Platform path instead of quietly
  // doing nothing — those have to go through `platformFeedApi`.
  [/^\/api\/feed\/posts\/create$/, 'feed/posts'],
  [/^\/api\/feed\/posts$/, 'feed/posts'],
  [/^\/api\/feed\/(comments|like|report|locations|categories|trending-tags|showcase|daily-stats|reports|likes)$/, 'feed/$1'],
];

/**
 * Legacy calls with no Platform counterpart yet. They are forwarded (so the 404 is
 * visible in the network panel and in the server log) but not toasted.
 */
const KNOWN_MISSING: RegExp[] = [
  /^utility\/columns$/, // grid column customization — no Platform table
  /^media\//, // media/upload service not built yet
  /^feed\/(categories|trending-tags|showcase|locations|daily-stats|reports|likes)$/,
];

/** Adds the Platform's product code, which the grant endpoints require. */
const PRODUCT_CODE = config.productCode;

const stripLeadingSlash = (value: string) => value.replace(/^\/+/, '');

/**
 * Splits `path?a=b` so a rewrite can be applied to the path alone.
 *
 * `axios` exposes the query in `config.params` for calls made through the instance, but the
 * ported screens also build query strings into the url by hand (e.g.
 * `` `/api/notification/read?id=${id}` ``), so both forms have to be understood.
 */
function splitUrl(url: string): { path: string; query: string } {
  const index = url.indexOf('?');
  return index < 0 ? { path: url, query: '' } : { path: url.slice(0, index), query: url.slice(index) };
}

function queryValue(query: string, key: string): string | null {
  if (!query) return null;
  const match = new RegExp(`[?&]${key}=([^&]*)`).exec(query);
  return match ? decodeURIComponent(match[1]) : null;
}

interface GrantPayload {
  resourceId?: string | number;
  read?: boolean;
  write?: boolean;
  canRead?: boolean;
  canWrite?: boolean;
}

/**
 * Legacy grant row → nothing. The reverse mapping (Platform → legacy) is in
 * `mapGrantsResponse`; this one only fixes the field names on the way out.
 */
function toPlatformGrants(grants: GrantPayload[]) {
  return grants.map((grant) => ({
    resourceId: grant.resourceId,
    canRead: grant.canRead ?? grant.read ?? false,
    canWrite: grant.canWrite ?? grant.write ?? false,
  }));
}

function mapRequest(request: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  let url = (request.url ?? '').trim();
  if (!url) return request;

  // 1. Rewrite the hard-coded legacy literals (absolute `/api/...` paths).
  const { path, query } = splitUrl(url.startsWith('/') ? url : `/${url}`);
  for (const [pattern, replacement] of LEGACY_PATH_REWRITES) {
    if (pattern.test(path)) {
      url = path.replace(pattern, replacement) + query;
      break;
    }
  }
  if (url.startsWith('/')) url = stripLeadingSlash(url);

  const method = (request.method ?? 'get').toLowerCase();
  const { path: barePath, query: bareQuery } = splitUrl(url);

  // 2. Verb + path-shape translations for the resource-shaped Platform routes.

  // `notifications/{id}/read` is a PUT on the Platform, a POST in the legacy client.
  if (method === 'post' && /^notifications\/[^/]+\/read$/.test(barePath)) {
    request.method = 'put';
  }

  // Category / user-category delete: legacy POSTs the id as a sub-path, the Platform
  // answers DELETE on the same resource. `save` and `listct` share that path shape but are
  // real POST routes, so they are excluded — turning them into DELETEs made every create,
  // edit and grid load on the two notification admin screens a 405.
  if (
    method === 'post' &&
    /^notifications\/(categories|user-categories)\/(?!(save|listct)$)[^/]+$/.test(barePath)
  ) {
    request.method = 'delete';
  }

  // Legacy `notifications/read?id=` (from `NotificationContext`) → PUT .../{id}/read.
  if (barePath === 'notifications/read') {
    const id = queryValue(bareQuery, 'id') ?? (request.params as { id?: string } | undefined)?.id;
    if (id !== undefined && id !== null && `${id}`.length > 0) {
      url = `notifications/${encodeURIComponent(String(id))}/read`;
      request.method = 'put';
      if (request.params) delete (request.params as Record<string, unknown>).id;
    }
  }

  // Role grants: legacy `authorization/role/grants?roleId=x` GET / POST-with-body.
  if (barePath === 'authorization/role/grants') {
    const params = (request.params ?? {}) as Record<string, unknown>;
    const body = (request.data ?? {}) as { roleId?: string; grants?: GrantPayload[] };
    const roleId = params.roleId ?? body.roleId;
    if (roleId !== undefined && roleId !== null && `${roleId}`.length > 0) {
      url = `authorization/roles/${encodeURIComponent(String(roleId))}/grants`;
      request.params = { productCode: PRODUCT_CODE };
      if (method === 'post') {
        request.method = 'put';
        request.data = { productCode: PRODUCT_CODE, grants: toPlatformGrants(body.grants ?? []) };
      }
    }
  }

  // Role delete: legacy `POST authorization/role/delete` with `{ Id }`.
  if (barePath === 'authorization/role/delete' && method === 'post') {
    const id = (request.data as { Id?: string } | undefined)?.Id;
    if (id) {
      url = `authorization/roles/${encodeURIComponent(String(id))}`;
      request.method = 'delete';
      request.data = undefined;
    }
  }

  // Role transfer: legacy `POST authorization/role/transfer` with both ids in the body.
  if (barePath === 'authorization/role/transfer' && method === 'post') {
    const body = (request.data ?? {}) as { roleSId?: string; roleTId?: string };
    if (body.roleTId) {
      url = `authorization/roles/${encodeURIComponent(String(body.roleTId))}/transfer`;
      request.data = { roleSId: body.roleSId, sourceRoleId: body.roleSId };
    }
  }

  // Role upsert: legacy `POST authorization/role/update`; `Id: 0` means create.
  if (barePath === 'authorization/role/update' && method === 'post') {
    const body = (request.data ?? {}) as { Id?: string | number; Code?: string; Name?: string };
    const id = body.Id;
    const payload = { Code: body.Code, Name: body.Name };
    if (id && `${id}` !== '0') {
      url = `authorization/roles/${encodeURIComponent(String(id))}`;
      request.method = 'put';
    } else {
      url = 'authorization/roles';
      request.method = 'post';
    }
    request.data = payload;
  }

  // Resource read: legacy `authorization/resources?resourceId=x` → `authorization/resources/{x}`.
  if (barePath === 'authorization/resources' && method === 'get') {
    const params = (request.params ?? {}) as Record<string, unknown>;
    if (params.resourceId !== undefined && params.resourceId !== null) {
      url = `authorization/resources/${encodeURIComponent(String(params.resourceId))}`;
      request.params = {};
    }
  }

  // Resource save: legacy `POST authorization/resources/save` → `PUT authorization/resources/{id}`.
  if (barePath === 'authorization/resources/save' && method === 'post') {
    const body = (request.data ?? {}) as { id?: string; title?: string; label?: string; url?: string };
    if (body.id) {
      url = `authorization/resources/${encodeURIComponent(String(body.id))}`;
      request.method = 'put';
      // `Code`, `Kind` and `ProductCode` define the catalog row's identity and are not
      // editable from this dialog even though the legacy payload carries `code`.
      request.data = { title: body.title, label: body.label, url: body.url };
    }
  }

  request.url = url;

  // 3. Flag the calls with no Platform counterpart so they do not toast on every render.
  if (KNOWN_MISSING.some((pattern) => pattern.test(stripLeadingSlash(url)))) {
    (request as { skipErrorToast?: boolean; skipLoader?: boolean }).skipErrorToast = true;
    (request as { skipLoader?: boolean }).skipLoader = true;
  }

  return request;
}

interface PlatformGrant {
  resourceId: string;
  code?: string;
  title?: string;
  label?: string;
  url?: string;
  canRead?: boolean;
  canWrite?: boolean;
}

/**
 * Platform grant row → the legacy `RoleGrant` the ported grid renders.
 *
 * `roleId` is echoed back because the legacy row type carries it and the grid's change
 * tracking compares whole rows; the Platform response does not repeat it per row.
 */
function mapGrantsResponse(response: AxiosResponse): AxiosResponse {
  const roleId = /authorization\/roles\/([^/]+)\/grants/.exec(response.config.url ?? '')?.[1];
  const rows = Array.isArray(response.data) ? (response.data as PlatformGrant[]) : [];
  response.data = rows.map((row) => ({
    roleId: roleId ? decodeURIComponent(roleId) : undefined,
    resourceId: row.resourceId,
    resourceCode: row.code ?? '',
    resourceTitle: row.title ?? '',
    resourceLabel: row.label ?? '',
    resourceUrl: row.url ?? '',
    read: !!row.canRead,
    write: !!row.canWrite,
  }));
  return response;
}

/** Platform role row → the legacy `IRole` (adds `companyId` / `isAdministrator`). */
function mapRolesResponse(response: AxiosResponse): AxiosResponse {
  const rows = Array.isArray(response.data) ? (response.data as Array<Record<string, unknown>>) : [];
  response.data = rows.map((row) => ({
    ...row,
    // The legacy screens filter and display roles by `companyId`; an organization is the
    // successor of a Company and a Platform session is scoped to exactly one, so the
    // catalogue's `organizationId` is that value. It is a GUID here where the legacy column
    // was an int, which the screens tolerate because they only ever compare it with itself.
    companyId: row.organizationId ?? row.companyId,
    isAdministrator: row.isSystem ?? row.isAdministrator,
  }));
  return response;
}

/**
 * Normalises the status a legacy screen branches on.
 *
 * `RoleInventory` and `UserInventory` both do `if (response.status === 200)`, while the
 * Platform correctly answers `201 Created` for a create and `204 No Content` for an
 * update. The screens are not wrong to check — the monolith always answered 200 — so the
 * status is normalised at the boundary rather than by editing them.
 */
function normaliseCreateUpdateStatus(response: AxiosResponse): AxiosResponse {
  const responseUrl = response.config.url ?? '';
  const isRoleWrite = /^authorization\/roles$/.test(responseUrl) || /^authorization\/roles\/[^/]+$/.test(responseUrl);
  if (!isRoleWrite) return response;
  if (response.status === 201 || response.status === 204) {
    response.status = 200;
    if (response.data === '' || response.data === null || response.data === undefined) response.data = {};
  }
  return response;
}

export function installPlatformApiAdapter(instance: AxiosInstance): void {
  /*
   * `axios.ts` installs these AFTER its auth/loader interceptors: axios runs request
   * interceptors last-registered-first, so this mapping runs before the loader interceptor and
   * the `skipLoader` / `skipErrorToast` flags set here are visible to it. See the install note
   * in `axios.ts`.
   */
  instance.interceptors.request.use(
    (request) => mapRequest(request),
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      const url = response.config.url ?? '';
      if (/^authorization\/roles\/[^/]+\/grants$/.test(url) || /^authorization\/role\/grants/.test(url)) {
        return normaliseCreateUpdateStatus(mapGrantsResponse(response));
      }
      if (/^authorization\/roles$/.test(url)) {
        return normaliseCreateUpdateStatus(mapRolesResponse(response));
      }
      return normaliseCreateUpdateStatus(response);
    },
    (error) => Promise.reject(error),
  );
}

export default installPlatformApiAdapter;
